import { NavLink, Outlet } from 'react-router-dom';
import { MarchioT2D } from './MarchioT2D.tsx';
import { useSchermoLargo } from './useSchermoLargo.ts';
import stili from './Guscio.module.css';

/**
 * Guscio dell'applicazione: navigazione e area di contenuto.
 *
 * Sul telefono la navigazione sta in basso, dove arriva il pollice. Sul
 * desktop diventa una barra laterale. Sono le stesse voci: cambia solo dove si
 * trovano, non cosa si puo' fare.
 *
 * Il calendario compare solo su schermi larghi. Non e' una limitazione tecnica ma
 * una scelta: una griglia da 365 colonne su un telefono non e' utilizzabile, e
 * offrirla comunque significa solo far perdere tempo a chi la apre.
 */

/**
 * I permessi che l'utente porta con se'.
 *
 * Arrivano gia' risolti dal server: il menu non ragiona sui ruoli, chiede
 * "questa persona puo' vedere questa pagina". Cosi' aggiungere un ruolo non
 * significa ritoccare anche questo file.
 */
export interface PermessiMenu {
  /** Un account di servizio non e' un dipendente: non ha una propria home. */
  home: boolean;
  anagrafica: boolean;
  decisioni: boolean;
  reportistica: boolean;
  funzionari: boolean;
}

interface Voce {
  percorso: string;
  etichetta: string;
  icona: string;
  soloDesktop?: boolean;
  /** Quale permesso serve per vederla. Assente: la vedono tutti. */
  permesso?: keyof PermessiMenu;
}

const VOCI: Voce[] = [
  { percorso: '/', etichetta: 'Home', icona: '🏠', permesso: 'home' },
  { percorso: '/da-decidere', etichetta: 'Da decidere', icona: '✔', permesso: 'decisioni' },
  { percorso: '/assenti', etichetta: 'Assenti', icona: '👥' },
  { percorso: '/calendario', etichetta: 'Calendario', icona: '▦', soloDesktop: true },
  {
    // Non solo desktop: sul telefono la pagina ha una vista sua, un giorno per
    // volta. Chi la cerca da fuori ufficio vuole sapere dove sara' qualcuno
    // domani, ed e' esattamente la domanda a cui risponde.
    percorso: '/funzionari',
    etichetta: 'Funzionari',
    icona: '🗓',
    permesso: 'funzionari',
  },
  {
    percorso: '/analisi',
    etichetta: 'Analisi dati',
    icona: '📊',
    soloDesktop: true,
    permesso: 'reportistica',
  },
  {
    percorso: '/anagrafica',
    etichetta: 'Anagrafica',
    icona: '👤',
    soloDesktop: true,
    permesso: 'anagrafica',
  },
];

export interface ProprietaGuscio {
  nomeUtente: string;
  permessi: PermessiMenu;
  /** Gli anni apribili. Il selettore compare solo se ce n'e' piu' di uno. */
  anni: readonly number[];
  anno: number;
  onAnno: (anno: number) => void;
  onEsci: () => void;
}

export function Guscio({ nomeUtente, permessi, anni, anno, onAnno, onEsci }: ProprietaGuscio) {
  const schermoLargo = useSchermoLargo();
  const voci = VOCI.filter(
    (v) =>
      (schermoLargo || !v.soloDesktop) && (v.permesso === undefined || permessi[v.permesso]),
  );

  return (
    <div className={stili.guscio}>
      <nav className={stili.navigazione} aria-label="Navigazione principale">
        <span className={stili.marchio}>
          <MarchioT2D className={stili.logo} />
          Assenze T2D
        </span>

        {/*
          Il selettore d'anno compare solo quando c'e' davvero un secondo anno
          da scegliere, cioe' da luglio in poi. Per meta' anno un menu con una
          voce sola sarebbe solo un elemento in piu' da capire.

          Sta nella barra laterale e non nella pagina del calendario perche'
          cambia i dati di tutta l'applicazione, non solo della griglia.
        */}
        {anni.length > 1 && (
          <label className={stili.anno}>
            <span className={stili.annoEtichetta}>Anno</span>
            <select
              className={stili.annoScelta}
              value={anno}
              onChange={(e) => onAnno(Number(e.target.value))}
            >
              {anni.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
        )}
        {voci.map((voce) => (
          <NavLink
            key={voce.percorso}
            to={voce.percorso}
            end={voce.percorso === '/'}
            className={({ isActive }) =>
              `${stili.voce} ${isActive ? stili.voceAttiva : ''}`
            }
          >
            <span className={stili.icona} aria-hidden="true">
              {voce.icona}
            </span>
            <span className={stili.testo}>{voce.etichetta}</span>
          </NavLink>
        ))}
        <button type="button" className={stili.esci} onClick={onEsci} title={nomeUtente}>
          <span className={stili.icona} aria-hidden="true">
            ⏻
          </span>
          <span className={stili.testo}>Esci</span>
        </button>
      </nav>

      <main className={stili.contenuto}>
        <Outlet />
      </main>
    </div>
  );
}
